import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRAYER_TYPES, DAY_TYPES, DAY_TYPE_SECTIONS } from '../../config/constants';
import { DayType, PrayerType, ScheduleEntry } from '../../types';
import { draftToTimeRule, TimeRuleDraft, timeRuleToDraft } from '../../utils/timeRuleDraft';
import { CANDLE_LIGHTING_ZMAN } from '../../utils/timeUtils';
import TimeRuleFields from './TimeRuleFields';

/** Prayer options offered for the current day-type selection. */
function prayersForDayTypes(dayTypes: DayType[]): PrayerType[] {
  // Hoshana Rabba is for a Shacharis that differs from Chol HaMoed (decided by Yoel, 2026-10-02).
  if (dayTypes.includes('hoshanaRabba')) return ['shacharit'];
  return PRAYER_TYPES.filter((p) => {
    // Tefilah Zakha belongs to Yom Kippur (Phase 6, day-types spec section 3).
    if (p === 'tefilahZakha') return dayTypes.includes('yomKippur');
    if (p === 'neila') return dayTypes.includes('yomKippur');
    if (p === 'tekiot') return dayTypes.includes('roshHashana') || dayTypes.includes('yomTov');
    if (p === 'megillah') return dayTypes.includes('purim') || dayTypes.includes('purimFriday');
    return true;
  });
}

export type ScheduleEntryFormData = Omit<ScheduleEntry, 'id' | 'overrides'>;

interface ScheduleEntryFormProps {
  entry?: ScheduleEntry;
  allSchedules?: ScheduleEntry[];
  onSave: (data: ScheduleEntryFormData) => void;
  onCancel: () => void;
}

export default function ScheduleEntryForm({
  entry,
  allSchedules = [],
  onSave,
  onCancel,
}: ScheduleEntryFormProps) {
  const { t } = useTranslation();
  const [prayerType, setPrayerType] = useState<PrayerType>(entry?.prayerType ?? 'shacharit');
  const [dayTypes, setDayTypes] = useState<DayType[]>(
    entry?.dayTypes?.length ? [...entry.dayTypes] : ['weekday']
  );
  const [timeRule, setTimeRule] = useState<TimeRuleDraft>(() => timeRuleToDraft(entry?.timeRule));
  const [active, setActive] = useState(entry?.active ?? true);
  const [note, setNote] = useState(entry?.note ?? '');
  const [noteHe, setNoteHe] = useState(entry?.noteHe ?? '');
  const [dayTypesError, setDayTypesError] = useState(false);
  const [specificDate, setSpecificDate] = useState(entry?.specificDate ?? '');
  const [specificDateError, setSpecificDateError] = useState(false);
  const [overridesEntryId, setOverridesEntryId] = useState(entry?.overridesEntryId ?? '');
  const [night, setNight] = useState<'start' | 'end' | ''>(entry?.night ?? '');
  const hasNight = prayerType === 'maariv' || prayerType === 'hakafot' || prayerType === 'megillah';

  const availablePrayers =
    prayerType === 'custom' ? PRAYER_TYPES : prayersForDayTypes(dayTypes);

  const weekdayModifierActive =
    dayTypes.includes('roshChodesh') || dayTypes.includes('sheniHamishi') || dayTypes.includes('chanukah');
  const overrideCandidates = allSchedules.filter(
    (s) =>
      s.prayerType === prayerType &&
      s.id !== entry?.id &&
      Array.isArray(s.dayTypes) &&
      s.dayTypes.includes('weekday')
  );
  const candidateLabel = (s: ScheduleEntry) =>
    s.note || s.noteHe || (s.timeRule.type === 'fixed' ? s.timeRule.time : t('admin.zmanBased'));

  useEffect(() => {
    if (prayerType === 'custom') return;
    const allowed = prayersForDayTypes(dayTypes);
    if (!allowed.includes(prayerType)) {
      setPrayerType(allowed[0] ?? 'shacharit');
    }
  }, [dayTypes, prayerType]);

  useEffect(() => {
    if (weekdayModifierActive && overrideCandidates.length === 1 && !overridesEntryId) {
      setOverridesEntryId(overrideCandidates[0].id);
    }
  }, [weekdayModifierActive, overrideCandidates, overridesEntryId]);

  const toggleDayType = (d: DayType) => {
    setDayTypesError(false);
    setDayTypes((prev) => {
      if (prev.includes(d)) {
        const next = prev.filter((x) => x !== d);
        return next;
      }
      // Preserve DAY_TYPES order for stable display
      const next = [...prev, d];
      return DAY_TYPES.filter((dt) => next.includes(dt));
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (prayerType === 'custom') {
      if (!specificDate) {
        setSpecificDateError(true);
        return;
      }
    } else if (dayTypes.length === 0) {
      setDayTypesError(true);
      return;
    }

    onSave({
      prayerType,
      dayTypes: prayerType === 'custom' ? [] : dayTypes,
      ...(prayerType === 'custom' ? { specificDate } : {}),
      timeRule: draftToTimeRule(timeRule),
      active,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(noteHe.trim() ? { noteHe: noteHe.trim() } : {}),
      ...(weekdayModifierActive && overridesEntryId ? { overridesEntryId } : {}),
      ...(hasNight && night ? { night } : {}),
      ...(prayerType === 'selichot' && entry?.selichotFrom ? { selichotFrom: entry.selichotFrom } : {}),
      ...(entry?.noMinyan ? { noMinyan: true } : {}),
    });
  };

  const inputClass =
    'w-full px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-fog rounded-[16px] border border-ink/10 p-4 space-y-3"
    >
      <div>
        <label htmlFor="schedule-prayer" className="block text-sm font-medium text-ink mb-1">
          {t('admin.prayerType')}
        </label>
        <select
          id="schedule-prayer"
          value={prayerType}
          onChange={(e) => {
            setPrayerType(e.target.value as PrayerType);
            setDayTypesError(false);
            setSpecificDateError(false);
          }}
          className={inputClass}
        >
          {availablePrayers.map((p) => (
            <option key={p} value={p}>
              {t(`prayers.${p}`)}
            </option>
          ))}
        </select>
      </div>

      {prayerType === 'custom' ? (
        <div>
          <label htmlFor="schedule-specific-date" className="block text-sm font-medium text-ink mb-1">
            {t('admin.specificDate')}
          </label>
          <input
            id="schedule-specific-date"
            type="date"
            required
            value={specificDate}
            onChange={(e) => {
              setSpecificDate(e.target.value);
              setSpecificDateError(false);
            }}
            className={`${inputClass} ${specificDateError ? 'border-danger-500' : ''}`}
          />
          {specificDateError && (
            <p className="mt-1.5 text-xs text-danger-600">{t('admin.selectSpecificDate')}</p>
          )}
        </div>
      ) : (
        <>
          <fieldset>
            <legend className="block text-sm font-medium text-ink mb-2">
              {t('admin.dayTypes')}
            </legend>
            <div
              className={`rounded-[10px] border bg-paper p-3 ${
                dayTypesError ? 'border-danger-500' : 'border-ink/10'
              }`}
            >
              {DAY_TYPE_SECTIONS.map((section, i) => (
                <div
                  key={section.key}
                  className={i > 0 ? 'mt-3 pt-3 border-t border-ink/10' : ''}
                >
                  <p className="text-xs font-medium text-ash uppercase tracking-wide mb-2">
                    {t(`admin.dayTypeSections.${section.key}`)}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {section.dayTypes.map((d) => {
                      const checked = dayTypes.includes(d);
                      return (
                        <label
                          key={d}
                          className="flex items-center gap-2 text-sm text-ink min-h-10 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDayType(d)}
                            className="rounded border-ink text-ink focus:ring-ink shrink-0"
                          />
                          <span>{t(`dayTypes.${d}`)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {dayTypesError && (
              <p className="mt-1.5 text-xs text-danger-600">{t('admin.selectAtLeastOneDayType')}</p>
            )}
          </fieldset>

          {weekdayModifierActive && overrideCandidates.length > 0 && (
            <div>
              <label htmlFor="schedule-overrides" className="block text-sm font-medium text-ink mb-1">
                {t('admin.replacesEntry')}
              </label>
              <select
                id="schedule-overrides"
                value={overridesEntryId}
                onChange={(e) => setOverridesEntryId(e.target.value)}
                className={inputClass}
              >
                <option value="">{t('admin.replacesEntryNone')}</option>
                {overrideCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {candidateLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <TimeRuleFields
        value={timeRule}
        onChange={(patch) => setTimeRule((prev) => ({ ...prev, ...patch }))}
        idPrefix="schedule"
        radioName="timeRuleType"
        extraZmanim={[{ key: CANDLE_LIGHTING_ZMAN, label: t('zmanim.candleLighting') }]}
      />

      {hasNight && (
        <div>
          <label htmlFor="schedule-night" className="block text-sm font-medium text-ink mb-1">
            {t('admin.night')}
          </label>
          <select
            id="schedule-night"
            value={night}
            onChange={(e) => setNight(e.target.value as 'start' | 'end' | '')}
            className={inputClass}
          >
            <option value="">{prayerType === 'maariv' ? t('admin.nightEnd') : t('admin.nightDay')}</option>
            <option value="start">{t('admin.nightStart')}</option>
            {prayerType === 'maariv' && <option value="end">{t('admin.nightEnd')}</option>}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="schedule-note-en" className="block text-sm font-medium text-ink mb-1">
          {t('admin.scheduleNoteEn')}
        </label>
        <input
          id="schedule-note-en"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('admin.scheduleNotePlaceholder')}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="schedule-note-he" className="block text-sm font-medium text-ink mb-1">
          {t('admin.scheduleNoteHe')}
        </label>
        <input
          id="schedule-note-he"
          type="text"
          value={noteHe}
          onChange={(e) => setNoteHe(e.target.value)}
          placeholder={t('admin.scheduleNotePlaceholder')}
          className={inputClass}
          dir="rtl"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="rounded border-ink text-ink focus:ring-ink"
        />
        {t('admin.active')}
      </label>

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          className="px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-[10px] text-sm text-ink hover:opacity-70 transition-opacity"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
